import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { useFamilyStore } from '../../store/useFamilyStore';
import { useFamilyMemberStore, FamilyMember } from '../../store/useFamilyMemberStore';
import { useUnitStore } from '../../store/useUnitStore';
import { Button, Label, Modal, TextInput, ToggleSwitch, Select } from 'flowbite-react';
import { HiOutlineExclamationCircle, HiX, HiPlus, HiPencil, HiTrash } from 'react-icons/hi';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

interface FamilyMemberForm {
  name: string;
  dob: string;
  education: string;
  job: string;
  baptismDate: string;
  firstCommunionDate: string;
  confirmationDate: string;
  married: boolean;
  marriedDate: string;
  deathDate: string;
  mother: string;
  father: string;
  contactNumbers: string[];
}

export default function FamilyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const getFamily = useFamilyStore((state) => state.getFamily);
  const updateFamily = useFamilyStore((state) => state.updateFamily);
  const getFamilyMembers = useFamilyMemberStore((state) => state.getFamilyMembers);
  const createFamilyMember = useFamilyMemberStore((state) => state.createFamilyMember);
  const updateFamilyMember = useFamilyMemberStore((state) => state.updateFamilyMember);
  const deleteFamilyMember = useFamilyMemberStore((state) => state.deleteFamilyMember);
  const members = useFamilyMemberStore((state) => state.members);
  const loading = useFamilyMemberStore((state) => state.loading);
  const getUnits = useUnitStore((state) => state.getUnits);
  const units = useUnitStore((state) => state.units);

  const [isEditMode, setIsEditMode] = useState(false);
  const [family, setFamily] = useState<any>(null);
  const [familyName, setFamilyName] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<string>('');
  const [guardian, setGuardian] = useState<FamilyMember | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteMemberId, setDeleteMemberId] = useState<string | null>(null);
  const [openEditMemberModal, setOpenEditMemberModal] = useState(false);
  const [openAddMemberModal, setOpenAddMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [openChangeGuardianModal, setOpenChangeGuardianModal] = useState(false);
  const [newGuardianId, setNewGuardianId] = useState<string>('');
  const [memberForm, setMemberForm] = useState<FamilyMemberForm>({
    name: '',
    dob: '',
    education: '',
    job: '',
    baptismDate: '',
    firstCommunionDate: '',
    confirmationDate: '',
    married: false,
    marriedDate: '',
    deathDate: '',
    mother: '',
    father: '',
    contactNumbers: [''],
  });

  useEffect(() => {
    if (id) {
      loadFamily();
      getUnits();
    }
  }, [id]);

  useEffect(() => {
    if (family) {
      loadMembers();
    }
  }, [family]);

  const loadFamily = async () => {
    if (!id) return;
    const familyData = await getFamily(id);
    if (familyData) {
      setFamily(familyData);
      setFamilyName(familyData.familyName);
      const unitId = typeof familyData.unit === 'string' ? familyData.unit : familyData.unit?._id || '';
      setSelectedUnit(unitId);
    }
  };

  const loadMembers = async () => {
    if (!id) return;
    await getFamilyMembers(id);
  };

  useEffect(() => {
    // Identify guardian from family.guardian field or fallback to earliest createdAt
    if (family?.guardian) {
      const guardianId = typeof family.guardian === 'string' ? family.guardian : family.guardian._id;
      const guardianMember = members.find((m) => m._id === guardianId);
      setGuardian(guardianMember || null);
    } else if (members.length > 0) {
      // Fallback: Identify guardian as the first member created (earliest createdAt)
      const guardianMember = members.reduce((oldest, member) => {
        const oldestDate = new Date(oldest.createdAt);
        const memberDate = new Date(member.createdAt);
        return memberDate < oldestDate ? member : oldest;
      });
      setGuardian(guardianMember);
    } else {
      setGuardian(null);
    }
  }, [members, family]);

  const handleUpdateFamily = async () => {
    if (!familyName || !selectedUnit) {
      toast.error('Please fill in all required fields.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    if (!id) return;
    await updateFamily(id, {
      familyName,
      unit: selectedUnit,
    });
    setIsEditMode(false);
    loadFamily();
  };

  const handleDeleteMember = async () => {
    if (!deleteMemberId) return;
    await deleteFamilyMember(deleteMemberId);
    setOpenDeleteModal(false);
    setDeleteMemberId(null);
    loadMembers();
  };

  const handleEditMember = (member: FamilyMember) => {
    setEditingMember(member);
    const motherId = typeof member.mother === 'string' ? member.mother : member.mother?._id || '';
    const fatherId = typeof member.father === 'string' ? member.father : member.father?._id || '';
    setMemberForm({
      name: member.name,
      dob: member.dob || '',
      education: member.education || '',
      job: member.job || '',
      baptismDate: member.baptismDate || '',
      firstCommunionDate: member.firstCommunionDate || '',
      confirmationDate: member.confirmationDate || '',
      married: member.married || false,
      marriedDate: member.marriedDate || '',
      deathDate: member.deathDate || '',
      mother: motherId,
      father: fatherId,
      contactNumbers: member.contactNumbers && member.contactNumbers.length > 0 ? member.contactNumbers : [''],
    });
    setOpenEditMemberModal(true);
  };

  const handleUpdateMember = async () => {
    if (!editingMember || !memberForm.name.trim()) {
      toast.error('Please fill in at least the member name.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    await updateFamilyMember(editingMember._id, {
      name: memberForm.name,
      dob: memberForm.dob || undefined,
      education: memberForm.education || undefined,
      job: memberForm.job || undefined,
      baptismDate: memberForm.baptismDate || undefined,
      firstCommunionDate: memberForm.firstCommunionDate || undefined,
      confirmationDate: memberForm.confirmationDate || undefined,
      married: memberForm.married,
      marriedDate: memberForm.marriedDate || undefined,
      deathDate: memberForm.deathDate || undefined,
      mother: memberForm.mother || undefined,
      father: memberForm.father || undefined,
      contactNumbers: memberForm.contactNumbers.filter((c) => c.trim() !== ''),
    });
    setOpenEditMemberModal(false);
    setEditingMember(null);
    loadMembers();
  };

  const handleAddMember = async () => {
    if (!memberForm.name.trim() || !id) {
      toast.error('Please fill in at least the member name.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    await createFamilyMember({
      name: memberForm.name,
      dob: memberForm.dob || undefined,
      education: memberForm.education || undefined,
      job: memberForm.job || undefined,
      baptismDate: memberForm.baptismDate || undefined,
      firstCommunionDate: memberForm.firstCommunionDate || undefined,
      confirmationDate: memberForm.confirmationDate || undefined,
      married: memberForm.married,
      marriedDate: memberForm.marriedDate || undefined,
      deathDate: memberForm.deathDate || undefined,
      mother: memberForm.mother || undefined,
      father: memberForm.father || undefined,
      contactNumbers: memberForm.contactNumbers.filter((c) => c.trim() !== ''),
      family: id,
    });
    setOpenAddMemberModal(false);
    setMemberForm({
      name: '',
      dob: '',
      education: '',
      job: '',
      baptismDate: '',
      firstCommunionDate: '',
      confirmationDate: '',
      married: false,
      marriedDate: '',
      deathDate: '',
      mother: '',
      father: '',
      contactNumbers: [''],
    });
    loadMembers();
  };

  const handleChangeGuardian = async () => {
    if (!newGuardianId || !id) {
      toast.error('Please select a new householder.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    try {
      await updateFamily(id, {
        guardian: newGuardianId,
      });
      setOpenChangeGuardianModal(false);
      setNewGuardianId('');
      loadFamily(); // Reload family to get updated guardian
      loadMembers(); // Reload members
      toast.success('Householder changed successfully');
    } catch (error) {
      toast.error('Failed to change householder');
    }
  };

  const updateMemberField = (field: keyof FamilyMemberForm, value: any) => {
    setMemberForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateContactNumber = (index: number, value: string) => {
    const newContacts = [...memberForm.contactNumbers];
    newContacts[index] = value;
    setMemberForm((prev) => ({ ...prev, contactNumbers: newContacts }));
  };

  const addContactNumber = () => {
    setMemberForm((prev) => ({ ...prev, contactNumbers: [...prev.contactNumbers, ''] }));
  };

  const removeContactNumber = (index: number) => {
    const newContacts = memberForm.contactNumbers.filter((_, i) => i !== index);
    setMemberForm((prev) => ({ ...prev, contactNumbers: newContacts.length > 0 ? newContacts : [''] }));
  };

  const regularMembers = members;

  // Calculate number of children for each member
  const getChildrenCount = (memberId: string) => {
    return members.filter((m) => {
      const motherId = typeof m.mother === 'string' ? m.mother : m.mother?._id || '';
      const fatherId = typeof m.father === 'string' ? m.father : m.father?._id || '';
      return motherId === memberId || fatherId === memberId;
    }).length;
  };

  if (!family) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Family Details" description="View and manage family information" />
      <PageBreadcrumb pageTitle="Family Details" />

      <div className="space-y-6">
        <ComponentCard title={isEditMode ? 'Edit Family' : 'Family Information'}>
          <div className="space-y-6">
            {/* Header with Edit Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {family.familyName}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {typeof family.unit === 'object' && family.unit ? `${family.unit.name} (${family.unit.unitNumber})` : 'No Unit'}
                </p>
              </div>
              {!isEditMode && (
                <Button
                  onClick={() => setIsEditMode(true)}
                  className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                  style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <HiPencil className="mr-2" /> Edit Family
                  </span>
                </Button>
              )}
            </div>

            {/* Family Information */}
            {isEditMode ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="familyName" value="Family Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <TextInput
                    id="familyName"
                    type="text"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                </div>
                <div>
                  <Label htmlFor="unit" value="Unit *" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <Select id="unit" value={selectedUnit} onChange={(e) => setSelectedUnit(e.target.value)} style={{ fontFamily: 'Lato, sans-serif' }}>
                    <option value="">Select Unit</option>
                    {units.map((unit) => (
                      <option key={unit._id} value={unit._id}>
                        {unit.name} ({unit.unitNumber})
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="col-span-2 flex gap-2">
                  <Button
                    onClick={handleUpdateFamily}
                    className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                    style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditMode(false);
                      setFamilyName(family.familyName);
                      const unitId = typeof family.unit === 'string' ? family.unit : family.unit?._id || '';
                      setSelectedUnit(unitId);
                    }}
                    color="gray"
                    style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label value="Family Name" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {family.familyName}
                  </p>
                </div>
                <div>
                  <Label value="Unit" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {typeof family.unit === 'object' && family.unit ? `${family.unit.name} (${family.unit.unitNumber})` : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label value="Created At" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {dayjs(family.createdAt).format('DD MMM YYYY, hh:mm A')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </ComponentCard>

        {/* Family Members */}
        <ComponentCard title="Family Members">
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                {guardian && (
                  <Button
                    onClick={() => setOpenChangeGuardianModal(true)}
                    size="sm"
                    className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                    style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Change Householder
                  </Button>
                )}
              </div>
              <Button
                onClick={() => {
                  setMemberForm({
                    name: '',
                    dob: '',
                    education: '',
                    job: '',
                    baptismDate: '',
                    firstCommunionDate: '',
                    confirmationDate: '',
                    married: false,
                    marriedDate: '',
                    deathDate: '',
                    mother: '',
                    father: '',
                    contactNumbers: [''],
                  });
                  setOpenAddMemberModal(true);
                }}
                size="sm"
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <HiPlus className="mr-2" /> Add Member
                </span>
              </Button>
            </div>
            {members.length > 0 ? (
              members.map((member) => {
                const isGuardian = guardian?._id === member._id;
                const childrenCount = getChildrenCount(member._id);
                const motherName = typeof member.mother === 'object' && member.mother ? member.mother.name : null;
                const fatherName = typeof member.father === 'object' && member.father ? member.father.name : null;

                return (
                  <div
                    key={member._id}
                    className={`border rounded-lg p-4 ${
                      isGuardian ? 'bg-[#B8860B]/10 border-2 border-[#B8860B] dark:bg-[#B8860B]/20 shadow-md' : 'bg-gray-50 dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                            {member.name}
                          </h3>
                          {isGuardian && (
                            <span
                              className="px-2 py-1 text-xs font-semibold rounded bg-[#B8860B] text-white"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              Householder
                            </span>
                          )}
                        </div>
                        {/* Parent Information */}
                        {(motherName || fatherName) && (
                          <div className="mt-2 flex flex-wrap gap-3 text-sm">
                            {motherName && (
                              <span className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                                <span className="font-medium">Mother:</span> {motherName}
                              </span>
                            )}
                            {fatherName && (
                              <span className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                                <span className="font-medium">Father:</span> {fatherName}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Children Count */}
                        {childrenCount > 0 && (
                          <div className="mt-1">
                            <span className="text-sm text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                              <span className="font-medium">Children:</span> {childrenCount}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleEditMember(member)}
                          size="sm"
                          className="bg-blue-500 text-white hover:bg-blue-600"
                          style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <HiPencil className="mr-1" /> Edit
                          </span>
                        </Button>
                        {!isGuardian && (
                          <Button
                            onClick={() => {
                              setDeleteMemberId(member._id);
                              setOpenDeleteModal(true);
                            }}
                            size="sm"
                            color="failure"
                            style={{
                              fontFamily: 'Lato, sans-serif',
                              height: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              <HiTrash className="mr-1" /> Delete
                            </span>
                          </Button>
                        )}
                        {isGuardian && (
                          <span className="text-xs text-[#B8860B] font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
                            Cannot delete householder
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Personal Information */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                        Personal Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {member.dob && (
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                                Date of Birth
                              </p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Lato, sans-serif' }}>
                                {dayjs(member.dob).format('DD MMM YYYY')}
                              </p>
                            </div>
                          </div>
                        )}
                        {member.education && (
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                                Education
                              </p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Lato, sans-serif' }}>
                                {member.education}
                              </p>
                            </div>
                          </div>
                        )}
                        {member.job && (
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                                Job
                              </p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Lato, sans-serif' }}>
                                {member.job}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sacramental Information */}
                    {(member.baptismDate || member.firstCommunionDate || member.confirmationDate) && (
                      <div className="mb-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                          Sacramental Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {member.baptismDate && (
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                                  Baptism Date
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Lato, sans-serif' }}>
                                  {dayjs(member.baptismDate).format('DD MMM YYYY')}
                                </p>
                              </div>
                            </div>
                          )}
                          {member.firstCommunionDate && (
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                                  First Communion
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Lato, sans-serif' }}>
                                  {dayjs(member.firstCommunionDate).format('DD MMM YYYY')}
                                </p>
                              </div>
                            </div>
                          )}
                          {member.confirmationDate && (
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                                  Confirmation Date
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Lato, sans-serif' }}>
                                  {dayjs(member.confirmationDate).format('DD MMM YYYY')}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Marital Status */}
                    {(member.married || member.marriedDate) && (
                      <div className="mb-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                          Marital Status
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded ${
                                member.married
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                              }`}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {member.married ? 'Married' : 'Single'}
                            </span>
                          </div>
                          {member.marriedDate && (
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                                  Married Date
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Lato, sans-serif' }}>
                                  {dayjs(member.marriedDate).format('DD MMM YYYY')}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Death Information */}
                    {member.deathDate && (
                      <div className="mb-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                          Death Information
                        </h4>
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                              Death Date
                            </p>
                            <p className="text-sm font-medium text-red-600 dark:text-red-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                              {dayjs(member.deathDate).format('DD MMM YYYY')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Contact Information */}
                    {member.contactNumbers && member.contactNumbers.length > 0 && (
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                          Contact Information
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {member.contactNumbers.map((contact, index) => (
                            <span
                              key={index}
                              className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {contact}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4" style={{ fontFamily: 'Lato, sans-serif' }}>
                No family members found
              </p>
            )}
          </div>
        </ComponentCard>
      </div>

      {/* Delete Member Modal */}
      <Modal show={openDeleteModal} size="md" popup onClose={() => setOpenDeleteModal(false)}>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete this family member?
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={handleDeleteMember} style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}>
                Yes, I'm sure
              </Button>
              <Button color="gray" onClick={() => setOpenDeleteModal(false)} style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}>
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Edit Member Modal */}
      <Modal show={openEditMemberModal} size="6xl" popup onClose={() => setOpenEditMemberModal(false)}>
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6 max-h-[85vh] overflow-y-auto">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Edit Family Member
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name" value="Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-name"
                  value={memberForm.name}
                  onChange={(e) => updateMemberField('name', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="edit-dob" value="Date of Birth" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-dob"
                  type="date"
                  value={memberForm.dob}
                  onChange={(e) => updateMemberField('dob', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="edit-education" value="Education" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-education"
                  value={memberForm.education}
                  onChange={(e) => updateMemberField('education', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="edit-job" value="Job" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-job"
                  value={memberForm.job}
                  onChange={(e) => updateMemberField('job', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="edit-baptismDate" value="Baptism Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-baptismDate"
                  type="date"
                  value={memberForm.baptismDate}
                  onChange={(e) => updateMemberField('baptismDate', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="edit-firstCommunionDate" value="First Communion Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-firstCommunionDate"
                  type="date"
                  value={memberForm.firstCommunionDate}
                  onChange={(e) => updateMemberField('firstCommunionDate', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="edit-confirmationDate" value="Confirmation Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-confirmationDate"
                  type="date"
                  value={memberForm.confirmationDate}
                  onChange={(e) => updateMemberField('confirmationDate', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="edit-mother" value="Mother" style={{ fontFamily: 'Lato, sans-serif' }} />
                <Select
                  id="edit-mother"
                  value={memberForm.mother}
                  onChange={(e) => updateMemberField('mother', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  <option value="">Select Mother (Optional)</option>
                  {members
                    .filter((m) => m._id !== editingMember?._id)
                    .map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-father" value="Father" style={{ fontFamily: 'Lato, sans-serif' }} />
                <Select
                  id="edit-father"
                  value={memberForm.father}
                  onChange={(e) => updateMemberField('father', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  <option value="">Select Father (Optional)</option>
                  {members
                    .filter((m) => m._id !== editingMember?._id)
                    .map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-marriedDate" value="Married Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-marriedDate"
                  type="date"
                  value={memberForm.marriedDate}
                  onChange={(e) => updateMemberField('marriedDate', e.target.value)}
                  disabled={!memberForm.married}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="edit-deathDate" value="Death Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="edit-deathDate"
                  type="date"
                  value={memberForm.deathDate}
                  onChange={(e) => updateMemberField('deathDate', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div className="flex items-center gap-2">
                <ToggleSwitch checked={memberForm.married} onChange={(checked) => updateMemberField('married', checked)} />
                <Label value="Married" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <div className="col-span-2">
                <Label value="Contact Numbers" style={{ fontFamily: 'Lato, sans-serif' }} />
                <div className="space-y-2">
                  {memberForm.contactNumbers.map((contact, contactIndex) => (
                    <div key={contactIndex} className="flex gap-2">
                      <TextInput
                        value={contact}
                        onChange={(e) => updateContactNumber(contactIndex, e.target.value)}
                        placeholder="Enter contact number"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      />
                      {memberForm.contactNumbers.length > 1 && (
                        <Button
                          onClick={() => removeContactNumber(contactIndex)}
                          size="sm"
                          color="failure"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '36px', height: '36px' }}
                        >
                          <HiX />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    onClick={addContactNumber}
                    size="sm"
                    className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                    style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <HiPlus className="mr-1" /> Add Contact
                    </span>
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleUpdateMember}
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Update Member
              </Button>
              <Button
                onClick={() => setOpenEditMemberModal(false)}
                color="gray"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Member Modal */}
      <Modal show={openAddMemberModal} size="6xl" popup onClose={() => setOpenAddMemberModal(false)}>
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6 max-h-[85vh] overflow-y-auto">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Add Family Member
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="add-name" value="Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-name"
                  value={memberForm.name}
                  onChange={(e) => updateMemberField('name', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="add-dob" value="Date of Birth" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-dob"
                  type="date"
                  value={memberForm.dob}
                  onChange={(e) => updateMemberField('dob', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="add-education" value="Education" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-education"
                  value={memberForm.education}
                  onChange={(e) => updateMemberField('education', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="add-job" value="Job" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-job"
                  value={memberForm.job}
                  onChange={(e) => updateMemberField('job', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="add-baptismDate" value="Baptism Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-baptismDate"
                  type="date"
                  value={memberForm.baptismDate}
                  onChange={(e) => updateMemberField('baptismDate', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="add-firstCommunionDate" value="First Communion Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-firstCommunionDate"
                  type="date"
                  value={memberForm.firstCommunionDate}
                  onChange={(e) => updateMemberField('firstCommunionDate', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="add-confirmationDate" value="Confirmation Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-confirmationDate"
                  type="date"
                  value={memberForm.confirmationDate}
                  onChange={(e) => updateMemberField('confirmationDate', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="add-mother" value="Mother" style={{ fontFamily: 'Lato, sans-serif' }} />
                <Select
                  id="add-mother"
                  value={memberForm.mother}
                  onChange={(e) => updateMemberField('mother', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  <option value="">Select Mother (Optional)</option>
                  {members.map((m) => (
                    <option key={m._id} value={m._id}>
                      {m.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="add-father" value="Father" style={{ fontFamily: 'Lato, sans-serif' }} />
                <Select
                  id="add-father"
                  value={memberForm.father}
                  onChange={(e) => updateMemberField('father', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  <option value="">Select Father (Optional)</option>
                  {members.map((m) => (
                    <option key={m._id} value={m._id}>
                      {m.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="add-marriedDate" value="Married Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-marriedDate"
                  type="date"
                  value={memberForm.marriedDate}
                  onChange={(e) => updateMemberField('marriedDate', e.target.value)}
                  disabled={!memberForm.married}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div>
                <Label htmlFor="add-deathDate" value="Death Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <TextInput
                  id="add-deathDate"
                  type="date"
                  value={memberForm.deathDate}
                  onChange={(e) => updateMemberField('deathDate', e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
              <div className="flex items-center gap-2">
                <ToggleSwitch checked={memberForm.married} onChange={(checked) => updateMemberField('married', checked)} />
                <Label value="Married" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <div className="col-span-2">
                <Label value="Contact Numbers" style={{ fontFamily: 'Lato, sans-serif' }} />
                <div className="space-y-2">
                  {memberForm.contactNumbers.map((contact, contactIndex) => (
                    <div key={contactIndex} className="flex gap-2">
                      <TextInput
                        value={contact}
                        onChange={(e) => updateContactNumber(contactIndex, e.target.value)}
                        placeholder="Enter contact number"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      />
                      {memberForm.contactNumbers.length > 1 && (
                        <Button
                          onClick={() => removeContactNumber(contactIndex)}
                          size="sm"
                          color="failure"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '36px', height: '36px' }}
                        >
                          <HiX />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    onClick={addContactNumber}
                    size="sm"
                    className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                    style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <HiPlus className="mr-1" /> Add Contact
                    </span>
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleAddMember}
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Add Member
              </Button>
              <Button
                onClick={() => setOpenAddMemberModal(false)}
                color="gray"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Change Householder Modal */}
      <Modal show={openChangeGuardianModal} size="md" popup onClose={() => setOpenChangeGuardianModal(false)}>
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-4">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Change Householder
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Select a new householder from the existing family members:
            </p>
            <div>
              <Label htmlFor="newGuardian" value="Select New Householder *" style={{ fontFamily: 'Lato, sans-serif' }} />
              <Select
                id="newGuardian"
                value={newGuardianId}
                onChange={(e) => setNewGuardianId(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <option value="">Select a member</option>
                {regularMembers.map((member) => (
                  <option key={member._id} value={member._id}>
                    {member.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleChangeGuardian}
                disabled={!newGuardianId}
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Change Householder
              </Button>
              <Button
                onClick={() => setOpenChangeGuardianModal(false)}
                color="gray"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
